/**
 * Watch Pick page behaviours: countdown timers and the watch-roll picker.
 * Loaded only on the watch-pick template. Slot / modal markup matches old_htmls classes
 * so ported watch-pick.css applies correctly.
 */
(function () {
  const prefersReducedMotion = () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  function twsRollTrack(eventName, extra) {
    if (typeof window.twsTrack !== "function") return;
    const roll =
      typeof window.twsTrackWatchRoll === "function" ? window.twsTrackWatchRoll() : {};
    window.twsTrack(eventName, Object.assign({}, roll, extra || {}));
  }

  function define(name, Class) {
    if (!customElements.get(name)) customElements.define(name, Class);
  }

  /** Bare ISO → UTC ms in an IANA zone (DST-aware). Offset/Z on the string wins. */
  function zonedWallTimeToUtc(dateStr, timeZone) {
    const m = String(dateStr)
      .trim()
      .match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (!m) return Date.parse(dateStr);

    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    const h = +(m[4] || 0);
    const mi = +(m[5] || 0);
    const s = +(m[6] || 0);
    const wanted = Date.UTC(y, mo - 1, d, h, mi, s);

    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });

    const partsOf = (ms) => {
      const out = {};
      for (const p of fmt.formatToParts(new Date(ms))) {
        if (p.type !== "literal") out[p.type] = p.value;
      }
      return out;
    };

    let utc = wanted;
    for (let i = 0; i < 3; i++) {
      const p = partsOf(utc);
      const asUtc = Date.UTC(
        +p.year,
        +p.month - 1,
        +p.day,
        +p.hour,
        +p.minute,
        +p.second
      );
      const diff = wanted - asUtc;
      if (diff === 0) break;
      utc += diff;
    }
    return utc;
  }

  function parseDeadline(raw, timeZone) {
    if (!raw) return NaN;
    const trimmed = String(raw).trim();
    if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed)) {
      return Date.parse(trimmed);
    }
    if (!timeZone || timeZone === "local") {
      return new Date(trimmed).getTime();
    }
    return zonedWallTimeToUtc(trimmed, timeZone);
  }

  class CountdownTimer extends HTMLElement {
    connectedCallback() {
      this.days = this.querySelector("[data-days]");
      this.hours = this.querySelector("[data-hours]");
      this.minutes = this.querySelector("[data-minutes]");
      this.seconds = this.querySelector("[data-seconds]");
      const raw = this.getAttribute("data-end");
      const timeZone = this.getAttribute("data-timezone");
      this.end = raw ? parseDeadline(raw, timeZone) : this.defaultEnd();
      if (Number.isNaN(this.end)) this.end = this.defaultEnd();
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    defaultEnd() {
      return Date.now() + 3 * 24 * 60 * 60 * 1000 + 6 * 60 * 60 * 1000;
    }

    tick() {
      const diff = Math.max(0, this.end - Date.now());
      const d = Math.floor(diff / 86400000);
      const h = Math.floor((diff % 86400000) / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (this.days) this.days.textContent = String(d);
      if (this.hours) this.hours.textContent = String(h).padStart(2, "0");
      if (this.minutes) this.minutes.textContent = String(m).padStart(2, "0");
      if (this.seconds) this.seconds.textContent = String(s).padStart(2, "0");
    }
  }

  define("countdown-timer", CountdownTimer);

  class WatchRollPicker extends HTMLElement {
    connectedCallback() {
      this.budget = Number(this.dataset.budget || 60000);
      this.bonusRate = Number(this.dataset.bonusRate || 0.8);
      this.maxSlots = Number(this.dataset.maxSlots || 3);
      this.currency = this.dataset.currency || "$";
      this.plansAnchor = this.dataset.plansAnchor || "#wp-plans";
      this.watches = this.parseWatches();
      this.slots = Array(this.maxSlots).fill(null);
      this.entryMode = "membership";
      this.openSlot = 0;
      this.locked = false;
      this._wpkScrollY = 0;

      this.cacheDom();
      /* Default: Choose later (membership) on all viewports */
      this.querySelectorAll("[data-entry]").forEach((btn) => {
        btn.classList.toggle("is-selected", btn.dataset.entry === this.entryMode);
      });
      this.mountModalToBody();
      this.renderSlots();
      this.updateBudget();
      this.updateEntryChrome();
      this.bind();
      /* Re-apply CTA on orientation / resize */
      this._ctaMedia = window.matchMedia("(max-width: 768px)");
      this._onCtaMediaChange = () => this.updateCta();
      this._ctaMedia.addEventListener("change", this._onCtaMediaChange);
    }

    disconnectedCallback() {
      if (this._ctaMedia && this._onCtaMediaChange) {
        this._ctaMedia.removeEventListener("change", this._onCtaMediaChange);
      }
      this.unlockPageScroll();
    }

    parseWatches() {
      let list = [];
      const srcId = this.dataset.watchesSrc || "wpk-watches-json";
      const scriptEl =
        document.getElementById(srcId) ||
        this.querySelector("[data-wpk-watches]") ||
        document.querySelector("[data-wpk-watches]");
      try {
        const raw = (scriptEl && scriptEl.textContent) || "[]";
        list = JSON.parse(raw.trim() || "[]");
      } catch {
        list = [];
      }
      if (!Array.isArray(list)) list = [];
      return list
        .map((w) => ({
          id: w.id,
          name: w.name,
          ref: w.ref || "",
          brand: w.brand || "",
          price: Number(w.price) || 0,
          sort: Number(w.sort) || 999,
          image: w.image || "",
          imageSrcset: w.imageSrcset || "",
        }))
        .filter((w) => w.id && w.price > 0)
        .sort(
          (a, b) =>
            a.sort - b.sort ||
            String(a.name || "").localeCompare(String(b.name || "")),
        );
    }

    getCheckoutPayload() {
      const filled = this.slots.filter(Boolean);
      return {
        entryMode: this.entryMode,
        budget: this.budget,
        currency: this.currency,
        used: this.used(),
        remaining: this.remaining(),
        ids: filled.map((w) => w.id).join(","),
        names: filled.map((w) => w.name).join(" | "),
        picks: filled.map((w) => ({
          id: w.id,
          name: w.name,
          ref: w.ref || "",
          brand: w.brand || "",
          price: w.price,
        })),
      };
    }

    publishRollState() {
      const payload = this.getCheckoutPayload();
      window.WPK_ROLL = payload;
      try {
        sessionStorage.setItem("wpk-roll", JSON.stringify(payload));
      } catch (_) {
        /* ignore */
      }
      try {
        document.dispatchEvent(
          new CustomEvent("wpk:roll-change", { detail: payload }),
        );
      } catch (_) {
        /* ignore */
      }
    }

    cacheDom() {
      this.slotsEl = this.querySelector("[data-slots]");
      this.labelsEl = this.querySelector("[data-slot-labels]");
      this.usedEl = this.querySelector("[data-used]");
      this.remEl = this.querySelector("[data-remaining]");
      this.fillEl = this.querySelector("[data-fill]");
      this.dotEl = this.querySelector("[data-dot]");
      this.cashCard = this.querySelector("[data-cash-card]") || this.querySelector("#wpk-cash");
      this.cashEl = this.querySelector("[data-cash]");
      this.ctaBtn = this.querySelector("[data-cta]");
      this.declineBtn = this.querySelector("[data-decline]");
      this.lockedEl = this.querySelector("[data-locked]");
      this.lockedDetail = this.querySelector("[data-locked-detail]");
      this.modal = this.querySelector("[data-modal]") || this.querySelector("#wpk-modal");
      this.grid = this.querySelector("[data-modal-grid]");
      this.search = this.querySelector("[data-search]");
      this.modalRem = this.querySelector("[data-modal-remaining]");
      this.modalGuidance = this.querySelector("[data-modal-guidance]");
      this.idsInput = this.querySelector("[data-input-ids]");
      this.namesInput = this.querySelector("[data-input-names]");
    }

    /** Like old WPK._mountModalToBody — fixed drawer needs real viewport (not transformed sections). */
    mountModalToBody() {
      if (!this.modal) return;
      if (this.modal.parentNode === document.body) return;
      try {
        document.body.appendChild(this.modal);
      } catch (_) {
        /* ignore */
      }
    }


    bind() {
      this.querySelectorAll("[data-entry]").forEach((btn) => {
        btn.addEventListener("click", () => this.setEntryMode(btn.dataset.entry));
      });
      this.ctaBtn?.addEventListener("click", () => this.confirm());
      this.declineBtn?.addEventListener("click", () => this.skip());
      this.querySelector("[data-edit]")?.addEventListener("click", () => this.edit());

      this.bindModalChrome();
      this.initWatchRequestForm();
      this.search?.addEventListener("input", () => {
        clearTimeout(this._searchTimer);
        this._searchTimer = setTimeout(() => {
          this.renderModalGrid();
          const term = (this.search?.value || "").trim();
          if (term) {
            twsRollTrack("searched_watch_catalog", { search_term: term });
          }
        }, 140);
      });
      this._onDocKeydown = (e) => {
        if (e.key === "Escape" && this.modal?.classList.contains("open")) {
          this.closeModal();
        }
      };
      document.addEventListener("keydown", this._onDocKeydown);
      /* Outside click hides slot ✕ (old WPK) */
      if (!this._rmvOutsideBound) {
        this._rmvOutsideBound = true;
        this._onDocClick = (e) => {
          if (!this._rmvOpenSlotEl) return;
          if (this.slotsEl?.contains(e.target)) return;
          this.hideAllSlotRemove();
        };
        document.addEventListener("click", this._onDocClick);
      }
    }

    bindModalChrome() {
      const m = this.modal;
      if (!m || m._wpkChromeBound) return;
      m._wpkChromeBound = true;
      m.querySelectorAll("[data-close-modal]").forEach((el) => {
        el.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
        });
      });
      /* Backdrop click (old WPK._oc) — shell stops propagation */
      m.addEventListener("click", (e) => {
        if (e.target === m) this.closeModal();
      });
      m.querySelector("[data-modal-shell]")?.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }

    money(n) {
      return `${this.currency}${Math.round(n).toLocaleString("en-US")}`;
    }

    used() {
      return this.slots.reduce((sum, w) => sum + (w ? Number(w.price) : 0), 0);
    }

    remaining() {
      return Math.max(0, this.budget - this.used());
    }

    hasPicks() {
      return this.slots.some(Boolean);
    }

    updateBudget() {
      const used = this.used();
      const rem = this.remaining();
      const pct = Math.min(100, (used / this.budget) * 100);
      if (this.usedEl) this.usedEl.textContent = this.money(used);
      if (this.remEl) this.remEl.textContent = this.money(rem);
      if (this.fillEl) this.fillEl.style.width = `${pct}%`;
      if (this.dotEl) this.dotEl.style.left = `${pct}%`;
      if (this.cashEl) this.cashEl.textContent = this.money(rem * this.bonusRate);
      if (this.modalRem) this.modalRem.textContent = this.money(rem);
      if (this.cashCard) {
        const show = this.hasPicks();
        this.cashCard.classList.toggle("on", show);
        if (show) {
          this.cashCard.hidden = false;
          this.cashCard.removeAttribute("hidden");
          this.cashCard.style.display = "";
        } else {
          this.cashCard.hidden = true;
          this.cashCard.setAttribute("hidden", "");
          this.cashCard.style.display = "none";
        }
      }
      this.syncInputs();
      this.updateCta();
      this.updateEntryChrome();
      this.updateModalGuidance();
    }

    syncInputs() {
      const filled = this.slots.filter(Boolean);
      if (this.idsInput) this.idsInput.value = filled.map((w) => w.id).join(",");
      if (this.namesInput) this.namesInput.value = filled.map((w) => w.name).join(" | ");
      this.publishRollState();
    }

    updateEntryChrome() {
      if (this.declineBtn) {
        const show = this.entryMode === "picks";
        if (show) {
          this.declineBtn.hidden = false;
          this.declineBtn.removeAttribute("hidden");
          this.declineBtn.style.display = "";
        } else {
          this.declineBtn.hidden = true;
          this.declineBtn.setAttribute("hidden", "");
          this.declineBtn.style.display = "none";
        }
      }
    }

    updateCta() {
      if (!this.ctaBtn) return;
      const count = this.slots.filter(Boolean).length;
      const isMobile = window.matchMedia("(max-width: 768px)").matches;
      this.ctaBtn.disabled = false;
      this.ctaBtn.removeAttribute("aria-disabled");

      if (this.entryMode === "membership") {
        this.ctaBtn.textContent = isMobile
          ? this.dataset.ctaOffer || "GET OFFER"
          : this.dataset.ctaMembership || "CLAIM MY OFFER";
        this.ctaBtn.dataset.ctaAction = "plans";
      } else if (count === 0) {
        this.ctaBtn.textContent =
          this.dataset.ctaIdle || "Pick a watch to start building";
        this.ctaBtn.dataset.ctaAction = "pick";
      } else {
        this.ctaBtn.textContent =
          this.dataset.ctaJoin || "JOIN WITH MY PICKS";
        this.ctaBtn.dataset.ctaAction = "plans";
      }
    }

    setEntryMode(mode) {
      const previous = this.entryMode;
      this.entryMode = mode;
      this.querySelectorAll("[data-entry]").forEach((btn) => {
        btn.classList.toggle("is-selected", btn.dataset.entry === mode);
      });
      if (previous !== mode) {
        twsRollTrack("selected_entry_method", {
          method: mode === "membership" ? "choose_later" : "build_lineup",
        });
      }
      if (mode === "picks" && previous !== "picks") {
        const empty = this.slots.findIndex((s) => !s);
        if (empty !== -1) this.openModal(empty);
      }
      this.updateCta();
      this.updateEntryChrome();
      this.publishRollState();
    }

    renderSlots() {
      if (!this.slotsEl) return;
      this.slotsEl.innerHTML = "";
      this.slots.forEach((watch, idx) => {
        const el = document.createElement("div");
        el.className = `wpk-slot${watch ? " has-watch" : ""}`;
        el.id = `wpk-slot-${idx}`;
        el.setAttribute("role", "button");
        el.tabIndex = 0;
        el.setAttribute(
          "aria-label",
          watch ? `Edit slot ${idx + 1}` : `Add watch to slot ${idx + 1}`,
        );

        if (watch) {
          const srcset = watch.imageSrcset
            ? ` srcset="${watch.imageSrcset}" sizes="120px"`
            : "";
          el.innerHTML = `
            <img class="wpk-slot-img" src="${watch.image || ""}"${srcset} alt="${watch.name || ""}" width="120" height="120" loading="lazy" decoding="async">
            <button type="button" class="wpk-slot-rmv" data-remove="${idx}" aria-label="Remove">✕</button>
          `;
          el.addEventListener("click", (e) => {
            if (e.target.closest("[data-remove]")) {
              e.stopPropagation();
              this.removeSlot(idx);
              return;
            }
            e.stopPropagation();
            this.toggleSlotRemove(el);
          });
        } else {
          el.innerHTML = `
            <div class="wpk-slot-ring"><span class="wpk-slot-plus" aria-hidden="true"></span></div>
          `;
          el.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.openModal(idx);
          });
        }

        el.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            if (watch) this.toggleSlotRemove(el);
            else this.openModal(idx);
          }
        });
        this.slotsEl.appendChild(el);
      });

      if (this.labelsEl) {
        const hasAny = this.hasPicks();
        this.labelsEl.hidden = !hasAny;
        this.labelsEl.classList.toggle("wpk-slot-labels--visible", hasAny);
        this.labelsEl.innerHTML = this.slots
          .map((w) => {
            if (!w) return `<div class="wpk-slot-lbl"></div>`;
            const ref = w.ref ? `Ref. ${w.ref}` : "";
            return `<div class="wpk-slot-lbl">
              <div class="ref">${ref}</div>
              <div class="name">${w.name || ""}</div>
              <div class="price">${this.money(w.price)}</div>
            </div>`;
          })
          .join("");
      }
    }

    hideAllSlotRemove() {
      this.slotsEl?.querySelectorAll(".wpk-slot.wpk-slot--rmv-show").forEach((el) => {
        el.classList.remove("wpk-slot--rmv-show");
      });
      this._rmvOpenSlotEl = null;
    }

    toggleSlotRemove(slotEl) {
      if (!slotEl?.classList.contains("has-watch")) return;
      const already = this._rmvOpenSlotEl === slotEl;
      this.hideAllSlotRemove();
      if (!already) {
        slotEl.classList.add("wpk-slot--rmv-show");
        this._rmvOpenSlotEl = slotEl;
      }
    }

    openModal(idx) {
      this.mountModalToBody();
      this.bindModalChrome();
      this.initWatchRequestForm();
      this.openSlot = typeof idx === "number" ? idx : 0;
      if (!this.modal) return;

      this.modal.hidden = false;
      this.modal.removeAttribute("hidden");
      this.modal.classList.add("open");
      this._wpkScrollY = window.scrollY || window.pageYOffset || 0;
      this.lockPageScroll();

      if (this.search) this.search.value = "";
      this.renderModalGrid();
      this.updateModalGuidance();
      requestAnimationFrame(() => {
        try {
          this.search?.focus({ preventScroll: true });
        } catch (_) {
          this.search?.focus();
        }
      });
      // Only this event — GTM also maps tws_watch_pick_modal/open to opened_watch_catalog.
      twsRollTrack("opened_watch_catalog", {
        slot_number: String(this.openSlot + 1),
      });
    }

    closeModal() {
      if (!this.modal) return;
      try {
        if (this.reqIsExpanded()) this.reqCollapse();
        this.reqResetForm();
      } catch (_) {
        /* ignore */
      }
      this.modal.classList.remove("open");
      this.modal.hidden = true;
      this.modal.setAttribute("hidden", "");
      this.unlockPageScroll();
      this.openSlot = -1;
      const y = this._wpkScrollY;
      if (typeof y === "number") {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => window.scrollTo(0, y));
        });
      }
    }

    lockPageScroll() {
      this._scrollLocked = true;
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    }

    unlockPageScroll() {
      this._scrollLocked = false;
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    }

    isModalOpen() {
      return !!(this.modal && this.modal.classList.contains("open"));
    }

    updateModalGuidance() {
      if (!this.modalGuidance) return;
      const count = this.slots.filter(Boolean).length;
      const rem = this.remaining();
      const slotsLeft = Math.max(0, this.maxSlots - count);
      const affordable = this.watches.filter((w) => {
        const inSlot = this.slots.some((s) => s && s.id === w.id);
        return !inSlot && w.price <= rem;
      }).length;

      if (!count) {
        this.modalGuidance.textContent = "Start by selecting your first watch.";
      } else if (affordable > 0 && slotsLeft > 0) {
        this.modalGuidance.textContent = `You can add ${slotsLeft} more ${slotsLeft === 1 ? "watch" : "watches"}. ${affordable} ${affordable === 1 ? "option fits" : "options fit"} your remaining budget`;
      } else if (slotsLeft === 0) {
        this.modalGuidance.innerHTML =
          'Woah, nice lineup! That\'s all 3 watches picked. Next step: <button type="button" class="wpk-modal-guidance-action" data-guidance-plans>choose your membership to enter</button>';
      } else {
        this.modalGuidance.innerHTML =
          'Woah, you\'ve used up the budget! Your lineup\'s ready — <button type="button" class="wpk-modal-guidance-action" data-guidance-plans>pick a membership next to enter the draw</button>';
      }
      this.modalGuidance
        .querySelector("[data-guidance-plans]")
        ?.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.closeModal();
          this.scrollToPlans();
        });
    }

    escHtml(s) {
      return String(s || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    }

    renderModalGrid() {
      if (!this.grid) return;
      const q = (this.search?.value || "").trim().toLowerCase();
      const selectedIds = new Set(this.slots.filter(Boolean).map((w) => w.id));
      const rem = this.remaining();
      const selectedCount = this.slots.filter(Boolean).length;
      const isLineupFull = selectedCount >= this.maxSlots;

      let list = this.watches.filter((w) => {
        if (!q) return true;
        return [w.name, w.ref, w.brand]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q));
      });

      if (selectedCount > 0) {
        list = list.slice().sort((a, b) => {
          const aIdx = this.slots.findIndex((s) => s && s.id === a.id);
          const bIdx = this.slots.findIndex((s) => s && s.id === b.id);
          const aIn = aIdx > -1;
          const bIn = bIdx > -1;
          const aOver = !aIn && a.price > rem;
          const bOver = !bIn && b.price > rem;
          const rankA = aIn ? 0 : aOver ? 2 : 1;
          const rankB = bIn ? 0 : bOver ? 2 : 1;
          if (rankA !== rankB) return rankA - rankB;
          if (aIn && bIn) return aIdx - bIdx;
          return (
            a.sort - b.sort ||
            String(a.name || "").localeCompare(String(b.name || ""))
          );
        });
      }
      // else: keep parseWatches() sort_order (already applied to this.watches)

      if (this.modalRem) this.modalRem.textContent = this.money(rem);
      this.updateModalGuidance();

      this.grid.innerHTML = "";
      if (!list.length) {
        const empty = document.createElement("div");
        empty.className = "wpk-modal-empty";
        empty.textContent = this.watches.length
          ? "No watches match your search."
          : "No watches available. Add watches as blocks in the theme editor.";
        this.grid.appendChild(empty);
        return;
      }

      list.forEach((w) => {
        const inRoll = selectedIds.has(w.id);
        const over = !inRoll && w.price > rem;
        const locked = !inRoll && isLineupFull;
        const dis = over || locked;
        const card = document.createElement("div");
        card.className = `wpk-wcard${inRoll ? " sel" : ""}${dis ? " dis" : ""}`;
        card.dataset.id = w.id;
        const src = w.image || "";
        const srcset = w.imageSrcset
          ? ` srcset="${this.escHtml(w.imageSrcset)}" sizes="160px"`
          : "";
        card.innerHTML =
          `<div class="wpk-wcard-img">` +
          (src
            ? `<img src="${this.escHtml(src)}"${srcset} alt="${this.escHtml(w.name || "")}" width="160" height="160" loading="lazy" decoding="async">`
            : "") +
          `<div class="wpk-wcard-inroll">In watch roll</div>` +
          `</div>` +
          `<div class="wpk-wcard-body">` +
          (w.ref
            ? `<div class="wpk-wcard-ref">Ref. ${this.escHtml(w.ref)}</div>`
            : "") +
          `<div class="wpk-wcard-name">${this.escHtml(w.name || "")}</div>` +
          (over
            ? `<div class="wpk-wcard-price-row"><div class="wpk-wcard-price over">${this.money(w.price)}</div><span class="wpk-wcard-ob-inline">Over budget</span></div>`
            : `<div class="wpk-wcard-price">${this.money(w.price)}</div>`) +
          `</div>`;
        if (!dis) {
          card.addEventListener("click", () => this.selectWatch(w.id));
        }
        this.grid.appendChild(card);
      });
    }

    selectWatch(id) {
      if (this.openSlot < 0) return;
      const watch = this.watches.find((w) => w.id === id);
      if (!watch) return;

      const existing = this.slots.findIndex((s) => s && s.id === id);
      let didAdd = false;
      if (existing > -1) {
        this.slots[existing] = null;
        twsRollTrack("removed_watch_from_slot", {
          slot_number: String(existing + 1),
          watch_name: watch.name || "",
        });
      } else {
        if (this.slots.filter(Boolean).length >= this.maxSlots) return;
        const cur = this.slots[this.openSlot]?.price || 0;
        if (this.used() - cur + watch.price > this.budget) return;
        const addedSlot = this.openSlot;
        this.slots[this.openSlot] = watch;
        didAdd = true;
        if (this.entryMode !== "picks") {
          this.entryMode = "picks";
          this.querySelectorAll("[data-entry]").forEach((btn) => {
            btn.classList.toggle("is-selected", btn.dataset.entry === "picks");
          });
          this.updateEntryChrome();
        }
        const nextEmpty = this.slots.findIndex((s) => !s);
        if (nextEmpty > -1) this.openSlot = nextEmpty;
        twsRollTrack("added_watch_to_slot", {
          slot_number: String(addedSlot + 1),
          watch_name: watch.name || "",
        });
      }

      this.renderSlots();
      this.updateBudget();
      this.renderModalGrid();
      this.updateModalGuidance();
      if (didAdd) {
        const body = this.modal?.querySelector(".wpk-modal-body");
        if (body) {
          try {
            body.scrollTo({ top: 0, behavior: "smooth" });
          } catch (_) {
            body.scrollTop = 0;
          }
        }
      }
    }

    removeSlot(idx) {
      const removed = this.slots[idx];
      this.slots[idx] = null;
      this.locked = false;
      if (this.lockedEl) this.lockedEl.hidden = true;
      this.hideAllSlotRemove();
      this.renderSlots();
      this.updateBudget();
      if (this.modal?.classList.contains("open")) {
        this.renderModalGrid();
        this.updateModalGuidance();
      }
      twsRollTrack("removed_watch_from_slot", {
        slot_number: String(idx + 1),
        watch_name: (removed && removed.name) || "",
      });
    }

    confirm() {
      if (this.entryMode === "picks" && !this.hasPicks()) {
        const empty = this.slots.findIndex((s) => !s);
        this.openModal(empty === -1 ? 0 : empty);
        return;
      }
      if (this.isModalOpen()) this.closeModal();
      this.unlockPageScroll();
      this.locked = this.hasPicks();
      if (this.lockedEl) {
        this.lockedEl.hidden = !this.locked;
        if (this.lockedDetail) {
          const names = this.slots
            .filter(Boolean)
            .map((w) => w.name)
            .join(", ");
          this.lockedDetail.textContent = names ? ` — ${names}` : "";
        }
      }
      this.scrollToPlans();
      twsRollTrack("clicked_watch_pick_cta", {
        action: "confirm_lineup",
        cta_label: this.ctaBtn ? this.ctaBtn.textContent.trim() : "JOIN WITH MY PICKS",
      });
    }

    skip() {
      if (this.isModalOpen()) this.closeModal();
      this.unlockPageScroll();
      this.slots = Array(this.maxSlots).fill(null);
      this.locked = false;
      if (this.lockedEl) this.lockedEl.hidden = true;
      this.hideAllSlotRemove();
      this.renderSlots();
      this.updateBudget();
      this.setEntryMode("membership");
      this.scrollToPlans();
      twsRollTrack("clicked_watch_pick_cta", {
        action: "skip_to_plans",
        cta_label: this.declineBtn ? this.declineBtn.textContent.trim() : "Choose later",
      });
    }

    edit() {
      this.locked = false;
      if (this.lockedEl) this.lockedEl.hidden = true;
      const empty = this.slots.findIndex((s) => !s);
      this.openModal(empty === -1 ? 0 : empty);
      twsRollTrack("clicked_watch_pick_cta", {
        action: "edit_lineup",
        cta_label: "edit",
      });
    }

    scrollToPlans() {
      const el = document.querySelector(this.plansAnchor);
      if (el) {
        el.scrollIntoView({
          behavior: prefersReducedMotion() ? "auto" : "smooth",
          block: "start",
        });
      }
    }

    /* ── Watch request panel (old WPK request UI, client-side) ── */
    initWatchRequestForm() {
      if (this._reqInited) return;
      const m = this.modal;
      if (!m) return;
      const names = m.querySelector("[data-req-names]");
      const submit = m.querySelector("[data-req-submit]");
      const panel = m.querySelector("[data-req-panel]");
      if (!names || !submit || !panel) return;
      this._reqInited = true;
      this._req = {
        saving: false,
        emailManual: false,
        expanded: false,
        els: {
          root: m.querySelector("[data-modal-request]"),
          panel,
          toggle: m.querySelector("[data-req-toggle]"),
          names,
          add: m.querySelector("[data-req-add]"),
          msg: m.querySelector("[data-req-msg]"),
          form: m.querySelector("[data-req-form]"),
          submit,
          email: m.querySelector("[data-req-email]"),
          emailWrap: m.querySelector("[data-req-email-wrap]"),
        },
      };
      panel.hidden = true;
      this.reqResetForm();
      this._req.els.toggle?.addEventListener("click", () => this.reqToggle());
      this._req.els.add?.addEventListener("click", () => this.reqAddRow());
      this._req.els.submit?.addEventListener("click", () => this.submitWatchRequest());
      if (this._req.els.email) {
        this._req.els.email.addEventListener("focus", () => {
          this._req.emailManual = true;
          this._req.els.emailWrap?.classList.remove("is-hidden");
        });
        this._req.els.email.addEventListener("input", () => {
          this._req.emailManual = true;
          this._req.els.emailWrap?.classList.remove("is-hidden");
          this.reqSyncSubmit();
        });
      }
    }

    reqIsExpanded() {
      return !!this._req?.els?.root?.classList.contains("is-expanded");
    }

    reqExpand() {
      const { root, panel, toggle } = this._req?.els || {};
      if (!root || !panel) return;
      panel.hidden = false;
      panel.removeAttribute("hidden");
      panel.setAttribute("aria-hidden", "false");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.add("is-expanded");
          this._req.expanded = true;
          toggle?.setAttribute("aria-expanded", "true");
          this.reqSyncEmailField();
          this.reqSyncSubmit();
        });
      });
    }

    reqCollapse() {
      const { root, panel, toggle } = this._req?.els || {};
      if (!root || !panel) return;
      root.classList.remove("is-expanded");
      this._req.expanded = false;
      toggle?.setAttribute("aria-expanded", "false");
      panel.setAttribute("aria-hidden", "true");
      setTimeout(() => {
        if (!this.reqIsExpanded()) panel.hidden = true;
      }, 360);
    }

    reqToggle() {
      if (this.reqIsExpanded()) this.reqCollapse();
      else this.reqExpand();
    }

    reqShowMsg(text, isError) {
      const msg = this._req?.els?.msg;
      if (!msg) return;
      if (!text) {
        msg.hidden = true;
        msg.textContent = "";
        msg.className = "wpk-modal-request-msg";
        return;
      }
      msg.hidden = false;
      msg.removeAttribute("hidden");
      msg.textContent = text;
      msg.className =
        "wpk-modal-request-msg" + (isError ? " is-error" : " is-success");
    }

    reqNameValues() {
      const namesEl = this._req?.els?.names;
      if (!namesEl) return [];
      return [...namesEl.querySelectorAll(".wpk-modal-request-input")]
        .map((inp) => inp.value.trim())
        .filter(Boolean);
    }

    reqSyncSubmit() {
      const btn = this._req?.els?.submit;
      if (!btn) return;
      let email = this._req.els.email
        ? String(this._req.els.email.value || "").trim()
        : "";
      const wrap = this._req.els.emailWrap;
      if (wrap?.classList.contains("is-hidden")) {
        email = this.detectPageEmail();
      }
      const blocked =
        this._req.saving || !this.reqNameValues().length || !email;
      btn.classList.toggle("is-disabled", blocked);
      btn.setAttribute("aria-disabled", blocked ? "true" : "false");
    }

    reqMaxRows() {
      const namesEl = this._req?.els?.names;
      const max = parseInt(namesEl?.getAttribute("data-max") || "3", 10);
      return max > 0 ? max : this.maxSlots || 3;
    }

    reqCreateRow(value) {
      const row = document.createElement("div");
      row.className = "wpk-modal-request-row";
      const input = document.createElement("input");
      input.type = "text";
      input.className = "wpk-modal-request-input";
      input.placeholder = "e.g. Rolex Submariner Date";
      input.autocomplete = "off";
      input.value = value || "";
      input.addEventListener("input", () => this.reqSyncSubmit());
      row.appendChild(input);
      return row;
    }

    reqRefreshRows() {
      const namesEl = this._req?.els?.names;
      if (!namesEl) return;
      const rows = [...namesEl.querySelectorAll(".wpk-modal-request-row")];
      const max = this.reqMaxRows();
      rows.forEach((row, index) => {
        row.querySelectorAll(".wpk-modal-request-remove").forEach((btn) =>
          btn.remove(),
        );
        let label = row.querySelector(".wpk-modal-request-row-label");
        if (!label) {
          label = document.createElement("span");
          label.className = "wpk-modal-request-row-label";
          row.insertBefore(label, row.firstChild);
        }
        label.textContent = `Watch ${index + 1}`;
        if (rows.length > 1) {
          const removeBtn = document.createElement("button");
          removeBtn.type = "button";
          removeBtn.className = "wpk-modal-request-remove";
          removeBtn.textContent = "Remove";
          removeBtn.addEventListener("click", () => {
            row.remove();
            if (!namesEl.querySelector(".wpk-modal-request-row")) {
              namesEl.appendChild(this.reqCreateRow(""));
            }
            this.reqRefreshRows();
          });
          row.appendChild(removeBtn);
        }
      });
      if (this._req.els.add) this._req.els.add.hidden = rows.length >= max;
      this.reqSyncSubmit();
    }

    reqAddRow() {
      const namesEl = this._req?.els?.names;
      if (!namesEl) return;
      const rows = namesEl.querySelectorAll(".wpk-modal-request-row");
      if (rows.length >= this.reqMaxRows()) return;
      namesEl.appendChild(this.reqCreateRow(""));
      this.reqRefreshRows();
      const all = namesEl.querySelectorAll(".wpk-modal-request-row");
      const last = all[all.length - 1];
      last?.querySelector(".wpk-modal-request-input")?.focus();
    }

    reqResetForm() {
      const namesEl = this._req?.els?.names;
      if (!namesEl) return;
      namesEl.innerHTML = "";
      namesEl.appendChild(this.reqCreateRow(""));
      if (this._req.els.add) this._req.els.add.hidden = false;
      this.reqRefreshRows();
      this.reqShowMsg("", false);
      this._req.saving = false;
      this._req.emailManual = false;
      this.reqSyncEmailField();
      this.reqSyncSubmit();
    }

    detectPageEmail() {
      const modal = this.modal;
      const own = this._req?.els?.email;
      const selectors = [
        'input[name="email"]',
        'input[type="email"]',
        "#email",
        '[name="contact[email]"]',
      ];
      for (const sel of selectors) {
        for (const el of document.querySelectorAll(sel)) {
          if (!el || el === own) continue;
          if (el.id === "wpk-modal-request-email") continue;
          if (modal?.contains(el)) continue;
          const val = String(el.value || "").trim();
          if (val && this.isValidEmail(val)) return val;
        }
      }
      return "";
    }

    isValidEmail(str) {
      str = String(str || "").trim();
      if (!str || str.indexOf("@") < 1) return false;
      const parts = str.split("@");
      if (parts.length !== 2 || !parts[0] || !parts[1]) return false;
      return parts[1].includes(".") && !parts[1].endsWith(".");
    }

    reqSyncEmailField() {
      const emailEl = this._req?.els?.email;
      const wrap = this._req?.els?.emailWrap;
      if (!emailEl || !wrap) return;
      if (this._req.emailManual || document.activeElement === emailEl) {
        wrap.classList.remove("is-hidden");
        return;
      }
      const detected = this.detectPageEmail();
      if (detected) {
        emailEl.value = detected;
        wrap.classList.add("is-hidden");
      } else {
        wrap.classList.remove("is-hidden");
      }
    }

    submitWatchRequest() {
      const btn = this._req?.els?.submit;
      if (btn?.classList.contains("is-disabled")) {
        this.reqShowMsg("Enter your email and at least one watch name.", true);
        return;
      }
      const names = this.reqNameValues();
      if (!names.length) {
        this.reqShowMsg("Enter at least one watch name.", true);
        return;
      }
      let email = this._req.els.email
        ? String(this._req.els.email.value || "").trim()
        : "";
      if (this._req.els.emailWrap?.classList.contains("is-hidden")) {
        email = this.detectPageEmail();
      }
      if (!this.isValidEmail(email)) {
        this.reqShowMsg("Enter a valid email address.", true);
        return;
      }
      this._req.saving = true;
      this.reqSyncSubmit();
      this.reqShowMsg("Submitting your watch request…", false);

      const payload = {
        email,
        watches: names,
        at: new Date().toISOString(),
      };
      try {
        sessionStorage.setItem("wpk-watch-request", JSON.stringify(payload));
        document.dispatchEvent(
          new CustomEvent("wpk:watch-request", { detail: payload }),
        );
        twsRollTrack("submitted_watch_request", {
          requested_watch: names,
          email_provided: email ? "true" : "false",
        });
      } catch (_) {
        /* ignore */
      }

      const endpoint =
        this._req.els.form?.getAttribute("data-req-endpoint") ||
        "/apps/watch-pick/request";

      fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({ email, watches: names }),
      })
        .then(async (res) => {
          const data = await res.json().catch(() => ({}));
          if (!res.ok || data.ok === false) {
            throw new Error(
              data.error || "Could not submit. Please try again.",
            );
          }
          this.reqShowMsg(
            "Your watch request has been submitted. We will be in touch.",
            false,
          );
        })
        .catch((err) => {
          this.reqShowMsg(
            err && err.message
              ? err.message
              : "Could not submit. Please try again.",
            true,
          );
        })
        .finally(() => {
          this._req.saving = false;
          this.reqSyncSubmit();
        });
    }
  }

  define("watch-roll-picker", WatchRollPicker);

  /* Hero steps — click scrolls to watch roll (no picks) or plans (has picks). */
  function smoothScrollTo(el) {
    if (!el) return;
    if (prefersReducedMotion()) {
      el.scrollIntoView({ behavior: "auto", block: "start" });
      return;
    }
    const startY = window.pageYOffset || document.documentElement.scrollTop || 0;
    const targetY = el.getBoundingClientRect().top + startY;
    const duration = 600;
    let startTs = null;

    function easeOutCubic(x) {
      return 1 - (1 - x) ** 3;
    }

    function step(ts) {
      if (startTs === null) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      window.scrollTo(0, startY + (targetY - startY) * easeOutCubic(p));
      if (p < 1) window.requestAnimationFrame(step);
    }

    window.requestAnimationFrame(step);
  }

  function hasWatchSelected() {
    const picker = document.querySelector("watch-roll-picker");
    if (picker && Array.isArray(picker.slots) && picker.slots.some(Boolean)) {
      return true;
    }
    try {
      const roll = JSON.parse(sessionStorage.getItem("wpk-roll") || "null");
      if (roll && Array.isArray(roll.picks) && roll.picks.length) return true;
    } catch (_) {
      /* ignore */
    }
    return false;
  }

  function scrollToWatchRoll() {
    const roll = document.querySelector(
      "#wpk .wpk-roll-outer, #wpk .wpk-roll-case, #wpk, #wp-watchroll",
    );
    if (roll) smoothScrollTo(roll);
  }

  function scrollToPlanCards() {
    const picker = document.querySelector("watch-roll-picker");
    if (picker && typeof picker.scrollToPlans === "function") {
      picker.scrollToPlans();
      return;
    }
    const plan = document.querySelector("#wp-plans, .plans-section");
    if (plan) smoothScrollTo(plan);
  }

  function trackHeroStepClick(destination, hasPicks, label) {
    if (typeof window.twsTrack !== "function") return;
    const labelText = String(label || "").slice(0, 120);
    window.twsTrack("clicked_hero_funnel_step", {
      destination,
      step_label: labelText,
      has_watch_selected: !!hasPicks,
      tws_hero_destination: destination,
      tws_has_watch_selected: !!hasPicks,
      tws_cta_zone: "hero_steps",
      tws_cta_label: labelText,
    });
  }

  document.addEventListener("click", (e) => {
    const stepEl = e.target && e.target.closest ? e.target.closest(".hero__step") : null;
    if (!stepEl || !stepEl.closest(".hero__steps")) return;

    const hasPicks = hasWatchSelected();
    const destination = hasPicks ? "plans" : "watch_pick";
    const label = (stepEl.textContent || "").replace(/\s+/g, " ").trim();
    trackHeroStepClick(destination, hasPicks, label);

    if (hasPicks) scrollToPlanCards();
    else scrollToWatchRoll();
  });
})();
